/* iter100.197 — Shared doctor-portal filter primitives.
 *
 * Extracted from the copy-pasted grammar that was introduced across
 * My Patients / My Appointments / Billing (both tabs) / My Cases /
 * Offers in iter100.195-196. Purpose:
 *
 *   1. Single source of truth for the search-input dimensions (h-9,
 *      text-[12px], placeholder inheriting the same size) so every
 *      surface renders identical height + type-size, including the
 *      placeholder — which is what the user specifically flagged.
 *   2. Cheap next-tweak: adding a "clear all filters" affordance,
 *      keyboard shortcut, or focus-ring change is now a one-file
 *      change instead of a five-file grep.
 *
 * Two components, composed by the calling page:
 *   • <FilterSearchBar>  — row 1: boxed container + rounded pill
 *                          search + optional right-side slot for
 *                          sort dropdowns / view toggles / action
 *                          buttons.
 *   • <FilterChipRow>    — row 2: dot + label + count pill chips.
 *                          Some pages hide the row conditionally
 *                          (Appointments in day/week/month view) so
 *                          it's kept separate rather than folded
 *                          into FilterSearchBar.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

/* Locked at module scope so every callsite renders pixel-identical.
   If you need to tweak height / font-size / placeholder color across
   the portal, change it HERE, not at the callsite.

   iter100.197 note — Shadcn's `<Input>` base component sets
   `text-base md:text-sm` which overrides any plain `text-[12px]` at
   md+ breakpoints (tailwind-merge can't resolve responsive vs
   non-responsive font-size collisions). So we ALSO set the md
   variant explicitly here to force 12px at every breakpoint —
   otherwise the search would render 14px on desktop and drift from
   the pill-search aesthetic the doctor sees on mobile. */
const SEARCH_INPUT_CLASSES =
    "pl-9 pr-8 h-9 rounded-full bg-secondary/40 " +
    "text-[12px] md:text-[12px] " +
    "placeholder:text-[12px] placeholder:text-muted-foreground " +
    "border-border focus:bg-card";

export function FilterSearchBar({
    query,
    setQuery,
    placeholder = "Search…",
    searchTestid,
    testid,
    rightSlot = null,
}) {
    return (
        <div
            className="bg-card border border-border rounded-xl px-3 py-2 flex items-center gap-3"
            data-testid={testid}
        >
            <div className="relative flex-1 min-w-0">
                <MagnifyingGlass
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
                <Input
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={SEARCH_INPUT_CLASSES}
                    data-testid={searchTestid}
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => setQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label="Clear search"
                    >
                        <X size={12} weight="bold" />
                    </button>
                )}
            </div>
            {rightSlot && <div className="flex items-center gap-2 shrink-0">{rightSlot}</div>}
        </div>
    );
}

/* Chip row primitive. Each chip is:
     { id, label, count, tone?: "primary" | "amber" | "rose" | "emerald" | "sky" | "slate" }
   `Icon` is optional — pages that use a chip-specific icon (e.g.
   /doctor/cases with the Storefront + Warning icons) pass it via
   `chip.Icon`. Everything else renders the default coloured dot. */
const TONE_ACTIVE = {
    primary: "bg-primary/10 border-primary/40 text-primary",
    amber:   "bg-amber-100 border-amber-300 text-amber-700",
    rose:    "bg-rose-100 border-rose-300 text-rose-700",
    emerald: "bg-emerald-100 border-emerald-300 text-emerald-700",
    sky:     "bg-sky-100 border-sky-300 text-sky-700",
    slate:   "bg-slate-100 border-slate-300 text-slate-700",
    violet:  "bg-violet-100 border-violet-300 text-violet-700",
};
const TONE_DOT = {
    primary: "bg-primary",
    amber:   "bg-amber-500",
    rose:    "bg-rose-500",
    emerald: "bg-emerald-500",
    sky:     "bg-sky-500",
    slate:   "bg-slate-500",
    violet:  "bg-violet-500",
};

export function FilterChipRow({ chips, value, onChange, testid, chipTestidPrefix }) {
    if (!chips || chips.length === 0) return null;
    return (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
            {chips.map((c) => {
                const active = value === c.id;
                const tone = c.tone || "primary";
                const activeCls = active
                    ? (TONE_ACTIVE[tone] || TONE_ACTIVE.primary)
                    : "bg-background border-border text-muted-foreground hover:bg-secondary";
                const dotCls = TONE_DOT[tone] || TONE_DOT.primary;
                return (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => onChange(c.id)}
                        data-testid={chipTestidPrefix ? `${chipTestidPrefix}-${c.id}` : undefined}
                        className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${activeCls}`}
                    >
                        {c.Icon
                            ? <c.Icon size={11} weight={active ? "fill" : "duotone"} />
                            : <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />}
                        {c.label}
                        {typeof c.count === "number" && (
                            <span className="text-[9px] font-bold bg-black/5 px-1.5 py-0.5 rounded-full tabular-nums min-w-[2rem] inline-block text-center">
                                {c.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
